import useAPI from "@toothfairy/shared-api/useApi";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import React, {
  Suspense,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import envConfig from "../../../envConfig";
import Batch from "../../API/Batch";
import batchLogHelper from "../../API/Batch/batchLogHelper";
import Files from "../../API/Files";
import Constants from "../../Constants";
import usePath from "../../Hooks/usePath";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { BatchJobLog } from "../../models";
import colorsMappings from "../Common/colorsMappings";
import downloader from "../Common/downloader";
import BatchScreen from "./BatchScreen";
import benchmarksHelper from "../../API/Benchmarks/benchmarksHelper";
dayjs.extend(isBetween);
const routes = [
  {
    key: "agentBenchMark",
    title: "Agents",
    routeId: "bm",
    shortTitle: "BM",
  },
  // {
  //   key: "agentBenchMark1",
  //   title: "Planner Agents (Enterprise )",
  //   routeId: "abm",
  //   shortTitle: "ABM",
  //   disabled: true,
  // },
  // {
  //   key: "agentBenchMark2",
  //   title: "Desktop Agents (coming soon)",
  //   routeId: "cbm",
  //   shortTitle: "CBM",
  //   disabled: true,
  // },
];
const BatchScreenContainer = ({ navigation }) => {
  const { t } = useTranslation();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { params } = usePath();
  const {
    getActiveWorkspace,
    handleTabSelection,
    getRawTabSelection,
    getPlanConfig,
    getBatchLogInfo,
  } = useWorkspaces();

  const { user } = AppUser.useUser();
  const AppToasty = AppToast.useToast();

  const {
    data: uploadedBatchData,
    loading: batchDataUploadInProgress,
    apiRequest: uploadBatchFile,
    response: uploadBatchFileResponse,
  } = useAPI(Batch.uploadBatchFile, envConfig);

  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);

  const [selectedTab, setSelectedTab] = useState(
    getRawTabSelection(routes, "training")
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [start, setStart] = useState(dayjs().startOf("month").toDate());
  const [end, setEnd] = useState(dayjs().endOf("month").toDate());
  const [fileSelector, SetFileSelector] = useState(null);
  const [statusFilter, setStatusFilter] = useState("noFilter");
  const [displayedLogs, setDisplayedLogs] = useState([]);
  const searchTimeoutRef = useRef(null);
  const [batchConfig, setBatchConfig] = useState({
    temperature:
      getActiveWorkspace()?.parsingConfig?.temperature || Constants.temperature,
    topP: getActiveWorkspace()?.parsingConfig?.topP || Constants.topP,
    maxTokens:
      getActiveWorkspace()?.parsingConfig?.maxTokens || Constants.maxTokens,
    frequencyPenalty:
      getActiveWorkspace()?.parsingConfig?.frequencyPenalty ||
      Constants.frequencyPenalty,
    presencePenalty:
      getActiveWorkspace()?.parsingConfig?.presencePenalty ||
      Constants.presencePenalty,
    fileUploadRequired: true,
    isQuestionGeneration: false,
    weights: {
      correctness: 80,
      reasoning: 15,
      context_usage: 5,
    },
    passingThreashold: 90,
  });
  useEffect(() => {
    if (getActiveWorkspace()?.parsingConfig && !batchConfig)
      setBatchConfig({
        ...getActiveWorkspace()?.parsingConfig,
        fileUploadRequired: true,
        isQuestionGeneration: false,
        name: batchConfig?.name || "",
        description: batchConfig?.description || "",
        weights: {
          correctness: 80,
          reasoning: 15,
          context_usage: 5,
        },
      });
  }, [
    JSON.stringify(getActiveWorkspace()?.parsingConfig),
    selectedTab?.routeId,
  ]);
  const loadBatchLogs = async (workspaceID) => {
    let resultLogs = await batchLogHelper?.getBatchLogsByWorkspaceID(
      workspaceID
    );
    resultLogs = resultLogs?.data?.batchJobLogsByWorkspace?.items?.filter(
      (u) => u?._deleted != true
    );
    resultLogs = resultLogs?.map((u) => {
      return {
        ...u,
        batchMeta: JSON.parse(u?.batchMeta),
        batchConfig: JSON.parse(u?.batchConfig),
      };
    });
    dispatch("SET_BATCH_LOGS", resultLogs);
  };
  const debouncedSearch = useCallback(
    (searchParams = {}) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        const currentStatus =
          searchParams.statusFilter !== undefined
            ? searchParams.statusFilter
            : statusFilter;
        const currentStart =
          searchParams.start !== undefined ? searchParams.start : start;
        const currentEnd =
          searchParams.end !== undefined ? searchParams.end : end;
        const currentSelectedTab =
          searchParams.selectedTab !== undefined
            ? searchParams.selectedTab
            : selectedTab;

        setDisplayedLogs(
          state?.batchLogs
            ?.filter(
              (u) =>
                u.type === currentSelectedTab?.routeId &&
                (u.status === currentStatus || currentStatus === "noFilter") &&
                dayjs(new Date(u._lastChangedAt)).isBetween(
                  currentStart,
                  currentEnd,
                  null,
                  "[]"
                )
            )
            ?.map((u) => {
              return {
                ...u,
                batchConfig:
                  typeof u?.batchConfig == "string"
                    ? JSON.parse(u?.batchConfig)
                    : u?.batchConfig,
                batchMeta:
                  typeof u?.batchMeta == "string"
                    ? JSON.parse(u?.batchMeta)
                    : u?.batchMeta,
              };
            })
        );
      }, 300);
    },
    [state?.batchLogs, statusFilter, start, end, selectedTab]
  );

  const handleFilterChange = (v) => {
    setStatusFilter(v);
    debouncedSearch({ statusFilter: v });
  };
  const handleFieldUpdate = async (id, property, value) => {
    try {
      const result = await batchLogHelper.updateBatchJobLog(id, [
        {
          property,
          value,
        },
      ]);
      const objectIndex = state?.batchLogs.findIndex((log) => log.id === id);
      if (objectIndex >= 0) {
        state.batchLogs[objectIndex] = result;
        dispatch("SET_BATCH_LOGS", state.batchLogs);
      }
    } catch (error) {
      throw error;
    }
  };
  const handleUpdateStartDate = (v) => {
    setStart(v);
    debouncedSearch({ start: v });
  };
  const handleUpdateEndDate = (v) => {
    setEnd(v);
    debouncedSearch({ end: v });
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const handleNoFileUpload = async () => {
    await uploadBatchFile({
      workspaceid: state?.activeWorkspaceID,
      type: "rc",
      userid: user?.id,
      token: getActiveWorkspace().workspaceToken,
      config: batchConfig,
    });
  };
  useEffect(() => {
    if (fileSelector) {
      handleFileSelect();
      SetFileSelector(null);
    }
  }, [fileSelector, handleFileSelect, handleNoFileUpload]);
  const handleBatchInitiation = async (v) => {
    try {
      console.log("values", v);
      const refinedValue = {
        name: `${v.name}-${
          state?.benchmarks?.find((u) => u?.id == v.benchmarkID)?.name
        }`,
        description: v.description,
        agentID: v.agentId,
        reviewerAgentID: v.reviewerAgentID,
        benchmarkID: v.benchmarkID,
        status: "dispatched",
        type: selectedTab?.routeId,
        workspaceID: getActiveWorkspace()?.id,
        batchConfig: JSON.stringify(v),
        createdBy: user?.id,
      };
      console.log("refinedValue", refinedValue);
      const batchLogResult = await batchLogHelper.createBatchJobLog(
        refinedValue
      );

      console.log("batchLogResult", batchLogResult);
      loadBatchLogs(getActiveWorkspace()?.id);
    } catch (error) {
      console.error("Error on batch initiation", error);
      AppToasty.show(t("batchParsingCreationFailedCaption"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };

  const handleCancelBatch = async (id) => {
    try {
      await batchLogHelper.deleteBatchJobLog(id);
      AppToasty.show(t("BatchDeletionCaption"), {
        placement: "top",
        type: "success",
      });
      const batchLogs = state?.batchLogs.filter((u) => u.id !== id);
      dispatch("SET_BATCH_LOGS", batchLogs);
    } catch (error) {
      AppToasty.show(t("BatchDeletionFailedCaption"), {
        placement: "top",
        type: "danger",
      });
    }
  };

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    handleTabSelection(tab, routes, "batch");
    debouncedSearch({ selectedTab: tab });
  };
  const handleSearch = () => {
    setDisplayedLogs(
      state?.batchLogs
        ?.filter(
          (u) =>
            // u.createdBy === user.id &&
            u.type === selectedTab?.routeId &&
            (u.status === statusFilter || statusFilter === "noFilter") &&
            dayjs(new Date(u._lastChangedAt)).isBetween(start, end, null, "[]")
        )
        ?.map((u) => {
          return {
            ...u,
            batchConfig:
              typeof u?.batchConfig == "string"
                ? JSON.parse(u?.batchConfig)
                : u?.batchConfig,
            batchMeta:
              typeof u?.batchMeta == "string"
                ? JSON.parse(u?.batchMeta)
                : u?.batchMeta,
          };
        })
    );
  };
  useEffect(() => {
    let validLogs = state?.batchLogs?.filter(
      (u) =>
        // u.createdBy === user.id &&
        u.type === selectedTab?.routeId
    );
    // find index of the active training log
    setDisplayedLogs(validLogs);
  }, [selectedTab?.routeId, JSON.stringify(state?.batchLogs)]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);
  const loadBenchmarks = async (workspaceID) => {
    try {
      let resultBenchmarks = await benchmarksHelper?.getBenchmarksByWorkspaceID(
        workspaceID
      );
      resultBenchmarks =
        resultBenchmarks?.data?.BenchmarksByWorkspace?.items?.filter(
          (u) => u?._deleted != true
        );
      dispatch("SET_BENCHMARKS", resultBenchmarks);
    } catch (error) {
      console.error("Error loading benchmarks:", error);
    } finally {
    }
  };
  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadBenchmarks(getActiveWorkspace()?.id);
    }
  }, [getActiveWorkspace()?.id]);

  const handleFileSelect = () => {
    if (Platform.OS === "web") {
      // e.preventDefault();
      fileSelector.value = null;
      fileSelector.click();
    } else {
      AppToasty.show(`Bulk parsing not available on ${Platform.OS}`, {
        type: "warning",
      });
    }
  };
  const handleBatchOutputDataDownload = async ({
    workspaceid,
    logId,
    type,
    selection,
  }) => {
    try {
      const result = await Promise.all(
        selection?.map((u) =>
          S3downloadUrlRequest({
            workspaceid,
            logId: logId,
            type: getBatchLogInfo(logId)?.type?.toLowerCase(),
            filename: Constants.BATCH_DOWNLOAD_OPTIONS.find(
              (item) => item.id === u
            ).filename,
            context: "batch",
          })
        )
      );

      downloader.download_files(
        result?.map((u, i) => {
          return {
            download: u.url,
            filename: "BT-" + logId + "-" + selection[i],
          };
        })
      );
    } catch (error) {
      console.error("Batch data download error", error);
    }
  };
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <BatchScreen
        handleFieldUpdate={handleFieldUpdate}
        onUpdateStartDate={handleUpdateStartDate}
        onUpdateEndDate={handleUpdateEndDate}
        onFilterChange={handleFilterChange}
        statusFilter={statusFilter}
        startDate={start}
        endDate={end}
        handleTabChange={handleTabChange}
        selectedTab={selectedTab}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        handleBatchInitiation={handleBatchInitiation}
        handleFileSelect={handleFileSelect}
        batchLogs={displayedLogs}
        logStatusColors={colorsMappings.logStatusColors}
        handleCancelBatch={handleCancelBatch}
        batchParsingInProgress={
          state?.batchLogs?.filter(
            (u) => u.status === "inProgress" || u.status === "dispatched"
          )?.length > 0
        }
        routes={routes}
        initialTabFromRouting={params.sectionId}
        batchConfig={batchConfig}
        setBatchConfig={setBatchConfig}
        handleBatchOutputDataDownload={handleBatchOutputDataDownload}
      />
    </Suspense>
  );
};

export default BatchScreenContainer;
